import * as React from 'react';
import { FC, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import LoadingAnimation from 'components/loading-animation';
import { Animation as LoyaltyPoppyTrophyAnimation } from 'components/lottie-animations/loyalty-poppy-trophy';
import SemiCircleProgress from 'components/progress-bar/semi-circle';
import { IImageFragment } from 'generated/sanity-graphql';
import useMediaQuery from 'hooks/use-media-query';
import { useLoyaltyContext } from 'state/loyalty';
import { useUIContext } from 'state/ui';

import {
  ENABLE_NEW_TIER_CELEBRATION_ANIMATION,
  METER_BG_COLOR,
  METER_DESKTOP_HEIGHT,
  METER_DESKTOP_WIDTH,
  METER_DIAMETER_WIDTH_ADJUSTMENT,
  METER_HEIGHT,
  METER_OUTER_STROKE_DESKTOP_WIDTH,
  METER_OUTER_STROKE_WIDTH,
  METER_STROKE_COLOR,
  METER_STROKE_DESKTOP_WIDTH,
  METER_STROKE_WIDTH,
  METER_WIDTH,
  MIN_PROGRESS_WHEN_REDEEMABLE,
  POINTS_METER_ANIMATION_DELAY,
} from './constants';
import {
  CircleProgressContainer,
  Container,
  MeterContainer,
  PointsContainer,
  PointsLabel,
  PointsText,
  PointsTextContainer,
} from './loyalty-points-meter-widget.styled';
import { MeterIcon } from './meter-icon';
import { RewardText } from './reward-text';
import { useCurrentAndNextRewardPoints } from './use-current-and-next-reward-points';

/**
 *
 * LoyaltyPointsMeterWidget shows a points meter
 *
 */
const LoyaltyPointsMeterWidget: FC<{ imageBackground?: { image?: IImageFragment } }> = ({
  imageBackground,
}) => {
  const bgImage = imageBackground?.image;
  const { formatMessage } = useIntl();
  const { loyaltyUser } = useLoyaltyContext();
  const isDesktop = useMediaQuery('desktop');
  const { buildImageUrl } = useUIContext();
  const {
    hasData: hasPointsData,
    hasTier,
    currentRewardPoints,
    nextRewardPoints,
  } = useCurrentAndNextRewardPoints();
  const userPoints = loyaltyUser?.points || 0;
  const hasReward = currentRewardPoints !== undefined;
  const [progressPercent, setProgressPercent] = useState(0);
  const [animationCompleted, setAnimationCompleted] = useState(false);
  const { targetPercent, nextTierPointsNeeded } = useMemo(() => {
    if (!currentRewardPoints) {
      return { targetPercent: 0, nextTierPointsNeeded: nextRewardPoints };
    }
    if (!nextRewardPoints) {
      return { targetPercent: 100 };
    }
    const pointsNeeded = nextRewardPoints - userPoints;
    const percent = (1 - pointsNeeded / (nextRewardPoints - currentRewardPoints)) * 100;
    return {
      targetPercent: percent > 0 ? percent : MIN_PROGRESS_WHEN_REDEEMABLE,
      nextTierPointsNeeded: pointsNeeded,
    };
  }, [currentRewardPoints, nextRewardPoints, userPoints]);

  useEffect(() => {
    // Animate progress after a short delay
    const timeoutId = setTimeout(() => {
      if (targetPercent > 0) {
        setProgressPercent(targetPercent);
      }
    }, POINTS_METER_ANIMATION_DELAY);
    return () => clearTimeout(timeoutId);
  }, [targetPercent]);

  const handleComplete = () => {
    setAnimationCompleted(true);
  };

  const loading = !loyaltyUser || !hasPointsData;

  const width = isDesktop ? METER_DESKTOP_WIDTH : METER_WIDTH;
  const height = isDesktop ? METER_DESKTOP_HEIGHT : METER_HEIGHT;
  const diameter = width - METER_DIAMETER_WIDTH_ADJUSTMENT;
  const strokeWidth = isDesktop ? METER_STROKE_DESKTOP_WIDTH : METER_STROKE_WIDTH;
  const outerStrokeWidth = isDesktop ? METER_OUTER_STROKE_DESKTOP_WIDTH : METER_OUTER_STROKE_WIDTH;
  const displayTierAnimation =
    ENABLE_NEW_TIER_CELEBRATION_ANIMATION && hasTier && !animationCompleted;

  return (
    <Container backgroundImage={bgImage ? buildImageUrl(bgImage) : ''}>
      <MeterContainer>
        <MeterIcon progress={progressPercent} />
        <CircleProgressContainer>
          <SemiCircleProgress
            strokeWidth={strokeWidth}
            outerStrokeWidth={outerStrokeWidth}
            strokeColor={METER_STROKE_COLOR}
            backgroundColor={METER_BG_COLOR}
            width={width}
            height={height}
            diameter={diameter}
            percentage={progressPercent}
          />
        </CircleProgressContainer>
        <PointsContainer containerHeight={height}>
          {loading ? (
            <LoadingAnimation fillColor={Styles.color.white} />
          ) : (
            <PointsTextContainer>
              {displayTierAnimation ? (
                <LoyaltyPoppyTrophyAnimation onComplete={() => handleComplete()} />
              ) : (
                <>
                  <PointsText>{loyaltyUser?.points}</PointsText>
                  <PointsLabel>{formatMessage({ id: 'points' })}</PointsLabel>
                </>
              )}
            </PointsTextContainer>
          )}
        </PointsContainer>
      </MeterContainer>
      <RewardText hasReward={hasReward} nextTierPointsNeeded={nextTierPointsNeeded} />
    </Container>
  );
};

export default LoyaltyPointsMeterWidget;
